import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  Button
} from '@mui/material';
import { Search as SearchIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import axios from 'axios';
import { Socket } from 'socket.io-client';

interface Message {
  body: string;
  timestamp: Date;
  direction: 'inbound' | 'outbound';
}

interface Conversation {
  phoneNumber: string;
  contactName?: string;
  lastMessage: Message;
  unreadCount: number;
  messageCount: number;
}

interface InboxProps {
  twilioNumber: string;
  socket: Socket | null;
  onConversationSelect: (phoneNumber: string, contactName?: string) => void;
  onAddContact: (phoneNumber: string) => void;
  selectedPhone?: string | null;
}

const Inbox: React.FC<InboxProps> = ({ twilioNumber, socket, onConversationSelect, onAddContact, selectedPhone }) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [filteredConversations, setFilteredConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadConversations(true);
    // Poll for new messages every 10 seconds
    const interval = setInterval(() => {
      loadConversations(false);
    }, 10000);
    return () => clearInterval(interval);
  }, [twilioNumber]);

  useEffect(() => {
    if (socket) {
      socket.on('newMessage', handleNewMessage);
      return () => { socket.off('newMessage', handleNewMessage); };
    }
  }, [socket, conversations]);

  useEffect(() => {
    filterConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, conversations]);

  const loadConversations = async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      setError('');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const num = user.twilioNumber;
      const url = num ? `/api/conversations?twilioNumber=${encodeURIComponent(num)}` : '/api/conversations';
      const response = await axios.get(url);
      if (response.data.message) setError(response.data.message);
      setConversations(response.data.conversations || []);
    } catch (err: any) {
      console.error('Error loading conversations:', err);
      setError(err.response?.data?.error || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const handleNewMessage = (message: any) => {
    const phoneNumber = message.direction === 'inbound' ? message.from : message.to;
    setConversations(prev => {
      const existingIndex = prev.findIndex(c => c.phoneNumber === phoneNumber);
      const newMessage: Message = { body: message.body, timestamp: new Date(message.timestamp), direction: message.direction };
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          lastMessage: newMessage,
          unreadCount: message.direction === 'inbound' ? updated[existingIndex].unreadCount + 1 : updated[existingIndex].unreadCount,
          messageCount: updated[existingIndex].messageCount + 1
        };
        const [conversation] = updated.splice(existingIndex, 1);
        return [conversation, ...updated];
      } else {
        return [{ phoneNumber, lastMessage: newMessage, unreadCount: message.direction === 'inbound' ? 1 : 0, messageCount: 1 }, ...prev];
      }
    });
  };

  const filterConversations = () => {
    if (!searchQuery.trim()) { setFilteredConversations(conversations); return; }
    const query = searchQuery.toLowerCase();
    setFilteredConversations(conversations.filter(conv =>
      conv.phoneNumber.toLowerCase().includes(query) ||
      (conv.contactName && conv.contactName.toLowerCase().includes(query)) ||
      (conv.lastMessage && conv.lastMessage.body && conv.lastMessage.body.toLowerCase().includes(query))
    ));
  };

  const formatTimestamp = (timestamp: Date) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'JUST NOW';
    if (diffMins < 60) return `${diffMins}M AGO`;
    if (diffHours < 24) return `${diffHours}H AGO`;
    if (diffDays < 2) return 'YESTERDAY';
    if (diffDays < 7) return `${diffDays}D AGO`;
    return date.toLocaleDateString().toUpperCase();
  };

  const truncateMessage = (text: string, maxLength: number = 60) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search */}
      <Box sx={{ p: 2, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search Contacts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#f5f5f9' } }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
          }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mx: 2, mb: 1 }}>{error}</Alert>}

      {/* Conversation List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {filteredConversations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'No conversations found' : 'No conversations yet'}
            </Typography>
          </Box>
        ) : (
          filteredConversations.map((conversation) => {
            const isSelected = selectedPhone === conversation.phoneNumber;
            return (
              <Box
                key={conversation.phoneNumber}
                onClick={() => onConversationSelect(conversation.phoneNumber, conversation.contactName)}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  borderLeft: isSelected ? '3px solid #6c5ce7' : '3px solid transparent',
                  bgcolor: isSelected ? '#f8f7ff' : 'transparent',
                  '&:hover': { bgcolor: isSelected ? '#f8f7ff' : '#fafafa' },
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2d3436', fontSize: 13 }}>
                    {conversation.contactName || conversation.phoneNumber}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999', fontSize: 10, fontWeight: 500, whiteSpace: 'nowrap', ml: 1 }}>
                    {formatTimestamp(conversation.lastMessage.timestamp)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {conversation.unreadCount > 0 && (
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#6c5ce7', flexShrink: 0 }} />
                  )}
                  <Typography variant="caption" sx={{ color: '#888', lineHeight: 1.3 }}>
                    {truncateMessage(conversation.lastMessage.body)}
                  </Typography>
                </Box>
                {!conversation.contactName && (
                  <Button
                    size="small"
                    startIcon={<PersonAddIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={(e) => { e.stopPropagation(); onAddContact(conversation.phoneNumber); }}
                    sx={{ mt: 0.5, textTransform: 'none', fontSize: 11, color: '#6c5ce7', p: 0, minWidth: 'auto' }}
                  >
                    Add
                  </Button>
                )}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default Inbox;
